import { Handshake, ShieldCheck } from "lucide-react";

/**
 * Parceiros e apoiadores do Instituto GA+.
 *
 * TODO (equipe): substituir cada `wordmark` pelo logo oficial usando
 *   { logo: "/uploads/partners/<arquivo>.svg" } — basta colocar o arquivo em
 *   /app/frontend/public/uploads/partners/ e referenciar aqui.
 * A renderização abaixo suporta os dois modos (logo ou wordmark tipográfico),
 * por isso adicionar os logos é substituição transparente.
 */
const PARTNERS = [
  {
    id: "karne-e-keijo",
    name: "Karne e Keijo",
    wordmark: { text: "Karne", accent: "& Keijo", font: "serif" },
    category: "Empresa parceira",
  },
  {
    id: "vivo",
    name: "Vivo",
    wordmark: { text: "vivo", font: "sans", tone: "purple" },
    category: "Telecomunicações",
  },
  {
    id: "bom-leite",
    name: "Bom Leite",
    wordmark: { text: "Bom", accent: "Leite", font: "sans", tone: "blue" },
    category: "Alimentação",
  },
  {
    id: "vitarella",
    name: "Vitarella",
    wordmark: { text: "Vitarella", font: "serif", tone: "red" },
    category: "Alimentação",
  },
  {
    id: "tintas-iquine",
    name: "Tintas Iquine",
    wordmark: { text: "IQUINE", font: "sans", tone: "orange" },
    category: "Indústria",
  },
  {
    id: "uninassau-ead",
    name: "UniNassau EAD",
    wordmark: { text: "UniNassau", accent: "EAD", font: "sans", tone: "ink" },
    category: "Educação",
  },
  {
    id: "defensoria-pe",
    name: "Defensoria Pública de Pernambuco",
    wordmark: { text: "Defensoria", accent: "Pública PE", font: "serif", tone: "ink" },
    category: "Parceria pública",
  },
  {
    id: "emergent",
    name: "Emergent",
    wordmark: { text: "Emergent", font: "sans", tone: "emerald" },
    category: "Parceiro tecnológico",
  },
];

const TONE_CLASS = {
  purple: "text-purple-600",
  blue: "text-blue-600",
  red: "text-red-600",
  orange: "text-orange-600",
  ink: "text-ga-ink",
  emerald: "text-ga-emerald_deep",
};

function PartnerCard({ p }) {
  const toneClass = TONE_CLASS[p.wordmark.tone] || "text-ga-ink";
  const fontClass = p.wordmark.font === "serif" ? "font-display" : "font-sans";

  return (
    <article
      data-testid={`parceiro-${p.id}`}
      className="group relative flex flex-col items-center justify-center text-center p-7 rounded-3xl bg-white border border-border hover:border-ga-ink/25 transition-all hover:-translate-y-0.5"
      title={p.name}
    >
      {p.logo ? (
        <img
          src={p.logo}
          alt={p.name}
          loading="lazy"
          className="max-h-14 w-auto object-contain grayscale opacity-75 group-hover:grayscale-0 group-hover:opacity-100 transition-all"
        />
      ) : (
        <div className={`${fontClass} ${toneClass} leading-none tracking-tight text-center`}>
          <span className="font-black text-2xl sm:text-[28px]">
            {p.wordmark.text}
          </span>
          {p.wordmark.accent && (
            <span className="block font-semibold text-sm sm:text-base text-ga-ink/60 mt-1 tracking-wide">
              {p.wordmark.accent}
            </span>
          )}
        </div>
      )}
      <div className="mt-5 text-[10px] uppercase tracking-[0.2em] text-ga-ink/40 font-bold">
        {p.category}
      </div>
    </article>
  );
}

export default function Parceiros() {
  return (
    <section
      id="parceiros"
      data-testid="parceiros-section"
      className="section-pad bg-white"
    >
      <div className="container-ga">
        <div className="max-w-2xl mb-12">
          <div className="pill border-ga-ink/10 text-ga-ink/70 bg-ga-ink/5 mb-5" data-testid="parceiros-pill">
            <Handshake className="w-3.5 h-3.5" />
            Nossos parceiros
          </div>
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-ga-ink">
            Parceiros que <span className="gradient-text-ga">acreditam</span> nessa missão.
          </h2>
          <p className="mt-4 text-ga-ink/60 text-base sm:text-lg">
            Empresas, instituições de ensino, parceiros públicos e tecnológicos que confiam
            no trabalho do Instituto GA+ e tornam possível o cuidado diário
            com tantas famílias em Pernambuco.
          </p>
        </div>

        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
          data-testid="parceiros-grid"
        >
          {PARTNERS.map((p) => (
            <PartnerCard key={p.id} p={p} />
          ))}

          {/* Chamada para novos parceiros */}
          <article
            data-testid="parceiros-cta-card"
            className="group relative flex flex-col items-center justify-center text-center p-7 rounded-3xl bg-ga-ink text-white border border-ga-ink hover:-translate-y-0.5 transition-transform"
          >
            <ShieldCheck className="w-8 h-8 text-ga-amber" />
            <div className="font-display font-bold text-xl mt-4 leading-tight">
              Sua marca<br />aqui
            </div>
            <a
              href="#contato"
              data-testid="parceiros-cta-link"
              className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-ga-amber hover:text-white transition-colors"
            >
              Quero apoiar →
            </a>
          </article>
        </div>

        <p
          className="mt-8 text-center text-xs text-ga-ink/45"
          data-testid="parceiros-disclaimer"
        >
          Logos exibidos em formato tipográfico até recebermos os arquivos
          oficiais de cada parceiro.
        </p>
      </div>
    </section>
  );
}
